import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Applicant, Lead, LeadEvent } from '@embrace-returns/shared';
import { api } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { PassOnPanel } from '../components/PassOnPanel';
import {
  copyText,
  employmentLabel,
  fmtDate,
  formatAddress,
  gbp,
  leadToText,
  mortgageLabel,
} from '../leadText';

const fmtDateTime = (d?: string) =>
  d
    ? new Date(d).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const EVENT_LABEL: Record<string, string> = {
  received: 'Returned from Simply Lending',
  viewed: 'Viewed',
  closed: 'Passed on',
  note_added: 'Note added',
  email_sent: 'Notification sent',
};

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => {
        if (await copyText(text)) {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-hair px-2.5 py-1 text-[12px] font-medium text-muted transition-colors hover:bg-gray-50 hover:text-ink"
    >
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
      {done ? 'Copied' : label}
    </button>
  );
}

function Row({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex gap-4 border-b border-gray-100 py-2.5 last:border-0">
      <div className="w-40 flex-shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted">
        {label}
      </div>
      <div className="text-[14px] text-ink">{value}</div>
    </div>
  );
}

function applicantText(a: Applicant): string {
  const parts = [
    `Name: ${a.fullName}`,
    a.email && `Email: ${a.email}`,
    a.phone && `Phone: ${a.phone}`,
    a.dateOfBirth && `Date of birth: ${fmtDate(a.dateOfBirth)}`,
    formatAddress(a.address) && `Address: ${formatAddress(a.address)}`,
    employmentLabel(a.employmentType) && `Employment: ${employmentLabel(a.employmentType)}`,
    a.jobTitle && `Job title: ${a.jobTitle}`,
    a.annualSalary != null && `Annual salary: ${gbp(a.annualSalary)}`,
  ].filter(Boolean);
  return parts.join('\n');
}

function ApplicantCard({ a, index }: { a: Applicant; index: number }) {
  return (
    <section className="rounded-xl border border-hair bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-[12px] font-bold uppercase tracking-wide text-muted">
          Applicant {index + 1}
        </h2>
        <CopyButton text={applicantText(a)} />
      </div>
      <Row label="Name" value={a.fullName} />
      <Row label="Email" value={a.email} />
      <Row label="Phone" value={a.phone} />
      <Row label="Date of birth" value={a.dateOfBirth ? fmtDate(a.dateOfBirth) : undefined} />
      <Row label="Address" value={formatAddress(a.address)} />
      <Row label="Employment" value={employmentLabel(a.employmentType)} />
      <Row label="Job title" value={a.jobTitle} />
      <Row label="Annual salary" value={a.annualSalary != null ? gbp(a.annualSalary) : undefined} />
    </section>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passOpen, setPassOpen] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const res = await api.leads.get(leadId);
      setLead(res.lead);
      setEvents([...res.events].sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this lead.');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="py-20 text-center text-[13px] text-muted">Loading…</div>;
  if (error && !lead) {
    return (
      <div className="px-6 py-10">
        <p className="text-[13px] text-red-600">{error}</p>
        <button onClick={() => navigate('/')} className="mt-3 text-[13px] text-navy hover:underline">
          Back to inbox
        </button>
      </div>
    );
  }
  if (!lead) return null;

  const ml = mortgageLabel(lead);
  // Fall back to the email when the name is blank (cleared on free-text pass-on).
  const ownerLabel = lead.embraceAdviserName || lead.embraceAdviserEmail;
  // A passed-on (Closed) lead wasn't "returned to" its current owner.
  const ownerVerb = lead.status === 'New' ? 'Returned to' : 'Passed on to';

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-[52px] flex-shrink-0 items-center justify-between border-b border-hair bg-white px-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-[13px] text-muted hover:text-ink"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Inbox
        </button>
        <div className="flex items-center gap-3">
          <CopyButton text={leadToText(lead)} label="Copy all" />
          <StatusBadge status={lead.status} />
        </div>
      </header>

      {/* Sticky pass-on action bar — always visible above the scrolling detail. */}
      {lead.status === 'New' && (
        <div className="flex-shrink-0 border-b border-hair bg-white">
          <div className="flex min-h-[52px] items-center justify-between gap-3 px-6 py-2.5">
            <span className="text-[13px] font-medium text-ink">
              {passOpen ? 'Pass this lead on' : 'Ready to pass this lead on?'}
            </span>
            {!passOpen && (
              <button
                onClick={() => setPassOpen(true)}
                className="rounded-lg bg-brand px-5 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                Mark as passed on
              </button>
            )}
          </div>

          {passOpen && (
            <div className="border-t border-hair px-6 py-4">
              <PassOnPanel
                leadId={lead.leadId}
                ownerEmail={lead.embraceAdviserEmail}
                onDone={() => {
                  setPassOpen(false);
                  void load();
                }}
                onCancel={() => setPassOpen(false)}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="mx-auto max-w-3xl space-y-5">
          <div>
            <h1 className="text-[22px] font-bold text-ink">{lead.customerName}</h1>
            <p className="mt-1 text-[13px] text-muted">
              {ownerVerb} {ownerLabel} · {fmtDateTime(lead.receivedAt)}
            </p>
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-600">{error}</p>
          )}

          {(lead.applicants ?? []).map((a, i) => (
            <ApplicantCard key={i} a={a} index={i} />
          ))}

          <section className="rounded-xl border border-hair bg-white p-5 shadow-sm">
            <h2 className="mb-2 text-[12px] font-bold uppercase tracking-wide text-muted">
              Mortgage
            </h2>
            <Row label="Lender" value={lead.mortgageProvider} />
            <Row label="Amount" value={lead.mortgageAmount != null ? gbp(lead.mortgageAmount) : undefined} />
            <Row label="Type" value={ml} />
            <Row label="Term" value={lead.mortgageTerm != null ? `${lead.mortgageTerm} months` : undefined} />
            <Row label="Submitted" value={lead.submittedAt ? fmtDate(lead.submittedAt) : undefined} />
          </section>

          {lead.dependants?.length ? (
            <section className="rounded-xl border border-hair bg-white p-5 shadow-sm">
              <h2 className="mb-2 text-[12px] font-bold uppercase tracking-wide text-muted">
                Dependants ({lead.dependants.length})
              </h2>
              <div className="text-[14px] text-ink">
                {lead.dependants.map((d) => fmtDate(d)).join(', ')}
              </div>
            </section>
          ) : null}

          <section className="rounded-xl border border-hair bg-white p-5 shadow-sm">
            <h2 className="mb-2 text-[12px] font-bold uppercase tracking-wide text-muted">
              Referral
            </h2>
            <Row
              label="Embrace adviser"
              value={
                lead.embraceAdviserName
                  ? `${lead.embraceAdviserName} (${lead.embraceAdviserEmail})`
                  : lead.embraceAdviserEmail
              }
            />
            <Row label="SL reference" value={lead.slReference} />
            <Row label="Introducer" value={lead.introducerCompany} />
            <Row
              label="Case manager"
              value={
                lead.caseManagerName || lead.caseManagerEmail
                  ? [lead.caseManagerName, lead.caseManagerEmail ? `(${lead.caseManagerEmail})` : '']
                      .filter(Boolean)
                      .join(' ')
                  : undefined
              }
            />
          </section>

          {lead.notes && (
            <section className="rounded-xl border-l-[3px] border-hair bg-[#f8fafc] p-4">
              <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
                Note from Simply Lending
              </h2>
              <p className="text-[14px] text-ink">{lead.notes}</p>
            </section>
          )}

          <section className="rounded-xl border border-hair bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-[12px] font-bold uppercase tracking-wide text-muted">History</h2>
            <ol className="space-y-3">
              {events.map((ev, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
                  <div>
                    <div className="text-[13px] font-medium text-ink">
                      {EVENT_LABEL[ev.eventType] ?? ev.eventType}
                      {ev.actorName ? ` · ${ev.actorName}` : ''}
                    </div>
                    <div className="text-[11.5px] text-muted">{fmtDateTime(ev.timestamp)}</div>
                    {ev.note && <div className="mt-0.5 text-[12.5px] text-ink">{ev.note}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
